import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  Search,
  Download,
  Bookmark,
  Globe,
  Layers,
  ShieldCheck,
  Library,
  ArrowRight,
  CheckCircle2,
  Loader2,
  CircleDashed,
  Sparkles,
} from 'lucide-react';

/* ── Placeholder page ──────────────────────────────────────────────────────
   The Digital E-Library is not built yet. The mobile app links here so users
   who tap the E-Library entry point land on something intentional instead of
   a dead end. Replace this page (not the /elibrary route) once the real
   product ships — the app's deep link should keep working unchanged.
   ────────────────────────────────────────────────────────────────────────── */

const UPCOMING = [
  {
    icon: Globe,
    title: 'Global Publications',
    description:
      'Open-access journals, papers, and reference titles curated for your syllabus — readable without leaving UNILinkd.',
  },
  {
    icon: Layers,
    title: 'Department Shelves',
    description:
      'Every department gets its own shelf of notes, question banks, and reading lists, organized by semester and subject.',
  },
  {
    icon: Search,
    title: 'Search That Understands',
    description:
      'Find a title by author, subject, ISBN, or a half-remembered line from the syllabus — across every shelf at once.',
  },
  {
    icon: Download,
    title: 'Read Offline',
    description:
      'Download a title once and keep reading through patchy hostel Wi-Fi, commutes, and exam week.',
  },
  {
    icon: Bookmark,
    title: 'Notes & Highlights',
    description:
      'Highlight, annotate, and bookmark as you read. Share a passage straight into a class channel or group.',
  },
  {
    icon: ShieldCheck,
    title: 'Institution Controlled',
    description:
      'Your librarian decides what gets published, who can access it, and how long borrowed material stays available.',
  },
];

const ROADMAP = [
  {
    phase: 'Phase 01',
    title: 'Catalog foundations',
    detail: 'Shelf structure, metadata model, and librarian publishing tools.',
    status: 'done' as const,
  },
  {
    phase: 'Phase 02',
    title: 'Reader & search',
    detail: 'In-app reader, offline downloads, and cross-shelf search.',
    status: 'active' as const,
  },
  {
    phase: 'Phase 03',
    title: 'Notes, sharing & analytics',
    detail: 'Highlights, passage sharing, and reading insights for departments.',
    status: 'planned' as const,
  },
];

const STATUS_META = {
  done: {
    icon: CheckCircle2,
    label: 'Complete',
    chip: 'bg-brand-blue/10 text-brand-blue',
    ring: 'border-brand-blue bg-brand-blue text-brand-white',
  },
  active: {
    icon: Loader2,
    label: 'In progress',
    chip: 'bg-brand-yellow text-brand-black',
    ring: 'border-brand-black bg-brand-yellow text-brand-black',
  },
  planned: {
    icon: CircleDashed,
    label: 'Planned',
    chip: 'bg-brand-black/5 text-brand-black/50',
    ring: 'border-brand-black/20 bg-brand-white text-brand-black/40',
  },
};

/** Decorative stack of book spines — pure CSS, no asset dependency. */
function BookStack() {
  const spines = [
    { label: 'JOURNALS', bg: 'bg-brand-blue', text: 'text-brand-white', w: 'w-[88%]' },
    { label: 'REFERENCE', bg: 'bg-brand-black', text: 'text-brand-yellow', w: 'w-full' },
    { label: 'SYLLABUS', bg: 'bg-brand-white', text: 'text-brand-black', w: 'w-[80%]' },
    { label: 'ARCHIVE', bg: 'bg-[#0066FF]', text: 'text-brand-white', w: 'w-[94%]' },
  ];

  return (
    <div className="relative mx-auto w-full max-w-md" aria-hidden="true">
      <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl" />
      <div className="unilinkd-float relative rounded-3xl bg-brand-white p-8 shadow-2xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-lg">
            <BookOpen className="h-6 w-6 text-brand-white" />
          </div>
          <span className="rounded-full bg-brand-yellow px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-brand-black">
            Preview
          </span>
        </div>

        <div className="space-y-3">
          {spines.map((spine) => (
            <div
              key={spine.label}
              className={`flex h-12 items-center rounded-xl px-4 shadow-md ring-1 ring-brand-black/5 ${spine.bg} ${spine.w}`}
            >
              <span className={`text-xs font-extrabold uppercase tracking-[0.2em] ${spine.text}`}>
                {spine.label}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-6 space-y-2">
          <div className="h-2 w-full rounded-full bg-brand-black/5">
            <div className="h-2 w-2/3 rounded-full bg-gradient-to-r from-brand-blue to-[#0066FF]" />
          </div>
          <p className="text-xs font-bold uppercase tracking-wide text-brand-black/40">
            Shelves being built
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ELibraryPage() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col bg-brand-yellow">
      {/* Local keyframes — the shared Tailwind config only ships accordion animations. */}
      <style>{`
        @keyframes unilinkd-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        .unilinkd-float { animation: unilinkd-float 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .unilinkd-float { animation: none; }
        }
      `}</style>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] py-12 md:py-16">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, #004CFF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="container relative z-10">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
            <div className="flex flex-col justify-center space-y-6">
              <div className="inline-flex w-fit items-center gap-2 rounded-full bg-brand-white px-4 py-2 shadow-lg">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-blue opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-blue" />
                </span>
                <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-brand-black">
                  Coming Soon
                </span>
              </div>

              <h1 className="text-5xl font-extrabold tracking-tight text-brand-black sm:text-6xl md:text-7xl">
                The UNILinkd E-Library
              </h1>

              <p className="text-xl font-medium text-brand-black md:text-2xl">
                One digital reading room for your campus — department resources, curated
                publications, and shared academic material, all inside the app you already use.
              </p>

              <p className="text-base font-medium text-brand-black/70">
                We&apos;re building it now. Until it opens, everything else on UNILinkd works
                exactly as it does today.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row">
                <Button
                  size="lg"
                  onClick={() => navigate({ to: '/connect' })}
                  className="group rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] text-lg font-bold text-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
                >
                  Get launch updates
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate({ to: '/services' })}
                  className="rounded-xl bg-brand-white text-lg font-bold text-brand-black shadow-lg transition-all hover:scale-105 hover:shadow-xl"
                >
                  Explore what&apos;s live
                </Button>
              </div>
            </div>

            <BookStack />
          </div>
        </div>
      </section>

      {/* ── What's coming ── */}
      <section className="bg-brand-white py-16">
        <div className="container">
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-4xl font-extrabold tracking-tight text-brand-black md:text-5xl">
              What we&apos;re building
            </h2>
            <p className="mx-auto max-w-2xl text-xl font-medium text-brand-black/70">
              A library that behaves like the rest of UNILinkd — organized by department,
              controlled by your institution, and usable on a phone.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {UPCOMING.map((item) => (
              <div
                key={item.title}
                className="group rounded-3xl bg-brand-white p-8 shadow-xl ring-1 ring-brand-black/5 transition-all hover:scale-[1.02] hover:shadow-2xl"
              >
                <div className="mb-5 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-lg transition-transform group-hover:scale-110 group-hover:rotate-3">
                  <item.icon className="h-8 w-8 text-brand-white" />
                </div>
                <h3 className="mb-2 text-xl font-bold text-brand-black">{item.title}</h3>
                <p className="text-base font-medium text-brand-black/70">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Roadmap ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] py-16">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              'linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF), linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF)',
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px',
          }}
        />
        <div className="container relative z-10">
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-4xl font-extrabold tracking-tight text-brand-black md:text-5xl">
              Where it stands
            </h2>
            <p className="text-xl font-medium text-brand-black/80">
              An honest look at the build — no fixed launch date yet.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {ROADMAP.map((step) => {
              const meta = STATUS_META[step.status];
              const StatusIcon = meta.icon;
              return (
                <div key={step.phase} className="rounded-3xl bg-brand-white p-8 shadow-xl">
                  <div className="mb-5 flex items-center justify-between gap-4">
                    <div
                      className={`flex h-11 w-11 items-center justify-center rounded-full border-2 ${meta.ring}`}
                    >
                      <StatusIcon
                        className={`h-5 w-5 ${step.status === 'active' ? 'animate-spin [animation-duration:3s]' : ''}`}
                      />
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wide ${meta.chip}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <p className="mb-1 text-xs font-extrabold uppercase tracking-[0.2em] text-brand-black/40">
                    {step.phase}
                  </p>
                  <h3 className="mb-2 text-xl font-bold text-brand-black">{step.title}</h3>
                  <p className="text-base font-medium text-brand-black/70">{step.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Available today ── */}
      <section className="bg-brand-white py-16">
        <div className="container">
          <div className="rounded-3xl bg-brand-yellow p-8 shadow-xl md:p-10">
            <div className="grid items-center gap-8 md:grid-cols-[auto,1fr,auto]">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-white shadow-lg">
                <Library className="h-8 w-8 text-brand-blue" />
              </div>
              <div>
                <h3 className="mb-2 text-2xl font-extrabold text-brand-black">
                  Looking for books on campus? That already works.
                </h3>
                <p className="text-base font-medium text-brand-black/80">
                  The Campus Library — a searchable catalog of your institution&apos;s physical
                  collection, with live availability and librarian-managed issue tracking — is live
                  in the app today. The E-Library is the separate, fully digital reading space
                  we&apos;re adding next.
                </p>
              </div>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate({ to: '/services' })}
                className="w-full rounded-xl bg-brand-white text-base font-bold text-brand-black shadow-lg transition-all hover:scale-105 hover:shadow-xl md:w-auto"
              >
                See all features
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-brand-yellow py-12">
        <div className="container">
          <div className="relative overflow-hidden rounded-3xl bg-brand-white p-8 text-center shadow-2xl md:p-12">
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 20% 50%, #004CFF 0%, transparent 50%), radial-gradient(circle at 80% 80%, #FFD836 0%, transparent 50%)',
              }}
            />
            <div className="relative z-10">
              <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-lg">
                <Sparkles className="h-7 w-7 text-brand-white" />
              </div>
              <h2 className="mb-3 text-4xl font-extrabold tracking-tight text-brand-black md:text-5xl">
                Want the E-Library on your campus first?
              </h2>
              <p className="mb-6 text-xl font-medium text-brand-black/70">
                Institutions that reach out now help shape the catalog, the permissions model, and
                the first shelves we open.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                <Button
                  size="lg"
                  onClick={() => navigate({ to: '/connect' })}
                  className="rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] text-lg font-bold text-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
                >
                  Contact Us
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate({ to: '/download' })}
                  className="rounded-xl bg-brand-white text-lg font-bold text-brand-black shadow-lg transition-all hover:scale-105 hover:shadow-xl"
                >
                  Download the App
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
